'use client';
import { upsellDismissedEvent, upsellViewedEvent, } from '#pplx/frontend/apps/ask/events/unified-upsell/analytics.js';
import { isCtaSupported } from '#pplx/frontend/apps/ask/hooks/upsells-hooks/handlers/executeCtaHandler.js';
import { getSidebarUpsellQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/upsellKeys.js';
import { getSidebarUpsell } from '#pplx/frontend/apps/ask/queries/upsell-queries/getSidebarUpsell.js';
import { markUpsellAsInteracted } from '#pplx/frontend/apps/ask/queries/upsell-queries/markUpsellAsInteracted.js';
import { buildUpsellSlotAnalytics, getUpsellSlotInstanceId, } from '#pplx/frontend/apps/ask/upsell-slots/UpsellSlotRenderPlan.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
const useSidebarUpsellQuery = (enabled) => {
    const { data: sidebarUpsell = null, isError, isLoading, } = useQuery({
        queryKey: getSidebarUpsellQueryKey(),
        queryFn: getSidebarUpsell,
        staleTime: Infinity,
        gcTime: Infinity,
        enabled,
    });
    return {
        sidebarUpsell,
        isError,
        isLoading,
    };
};
const resolveSidebarUpsellSlotPlan = ({ input, data, controls, }) => {
    if (input.callsite.enabled === false || input.callsite.surface?.isSidebarOpen === false) {
        return { status: 'empty', show: false, reason: 'disabled' };
    }
    if (data.isLoading) {
        return { status: 'loading', show: false, reason: 'fetch_loading', isLoading: true };
    }
    if (data.isError) {
        return { status: 'error', show: false, reason: 'fetch_error', isError: true };
    }
    const { upsellInformation } = data;
    if (!upsellInformation) {
        return { status: 'empty', show: false, reason: 'no_backend_upsell' };
    }
    if (!upsellInformation.app_location ||
        !input.acceptedLocations.includes(upsellInformation.app_location)) {
        return { status: 'blocked', show: false, reason: 'location_mismatch' };
    }
    if (upsellInformation.cta && !isCtaSupported(upsellInformation.cta)) {
        return { status: 'blocked', show: false, reason: 'unsupported_cta' };
    }
    if (data.hiddenUpsellInstanceId === getUpsellSlotInstanceId(upsellInformation)) {
        return { status: 'blocked', show: false, reason: 'dismissed' };
    }
    if (!upsellInformation.title || !upsellInformation.button_text) {
        return { status: 'blocked', show: false, reason: 'missing_required_fields' };
    }
    return {
        status: 'ready',
        show: true,
        slotId: input.slotId,
        upsellInformation,
        render: {
            kind: 'sidebar_card',
            componentKey: 'SidebarUpsellCard',
            props: {
                upsellInformation,
                onDismiss: controls.onDismiss,
            },
        },
        actions: {
            ...controls,
            primaryCta: {
                upsellInformation,
                textButtonIfSecondary: true,
            },
        },
        analytics: buildUpsellSlotAnalytics(input.slotId, upsellInformation),
    };
};
export const useSidebarUpsellSlotPlan = (input) => {
    const enabled = input.callsite.enabled !== false && input.callsite.surface?.isSidebarOpen !== false;
    const { sidebarUpsell, isError, isLoading } = useSidebarUpsellQuery(enabled);
    const { track, impression } = useInstrumentation();
    const queryClient = useQueryClient();
    const [visible, setVisible] = useState(false);
    const [hiddenUpsellInstanceId, setHiddenUpsellInstanceId] = useState(null);
    const hideUpsell = useCallback(() => {
        if (!sidebarUpsell)
            return;
        setVisible(false);
    }, [sidebarUpsell]);
    const onDismiss = useCallback(async () => {
        if (!enabled || !sidebarUpsell)
            return;
        setVisible(false);
        setHiddenUpsellInstanceId(getUpsellSlotInstanceId(sidebarUpsell));
        track(upsellDismissedEvent({
            name: 'upsell dismissed',
            data: {
                upsellUUID: sidebarUpsell.upsell_uuid ?? '',
                upsellName: sidebarUpsell.name ?? '',
                eventMetadata: sidebarUpsell.event_metadata,
                upsellLocation: sidebarUpsell.app_location ?? '',
                upsellType: sidebarUpsell.upsell_type ?? '',
            },
        }));
        queryClient.setQueryData(getSidebarUpsellQueryKey(), null);
        // Keep the null cache entry so active sidebar observers do not refetch or hydrate
        // the dismissed upsell on the next render.
        await markUpsellAsInteracted({
            upsellName: sidebarUpsell.name ?? '',
            upsellUUID: sidebarUpsell.upsell_uuid,
            interactionType: 'dismiss',
        });
    }, [enabled, queryClient, sidebarUpsell, track]);
    const sourcePlan = resolveSidebarUpsellSlotPlan({
        input,
        data: {
            upsellInformation: sidebarUpsell,
            hiddenUpsellInstanceId,
            isError,
            isLoading,
        },
        controls: {
            onDismiss,
            hideUpsell,
        },
    });
    const readyUpsellInformation = sourcePlan.show ? sourcePlan.upsellInformation : null;
    useEffect(() => {
        if (!enabled || !readyUpsellInformation) {
            return;
        }
        setVisible(true);
        impression(upsellViewedEvent({
            name: 'upsell viewed',
            data: {
                upsellUUID: readyUpsellInformation.upsell_uuid ?? '',
                upsellName: readyUpsellInformation.name ?? '',
                eventMetadata: readyUpsellInformation.event_metadata,
                upsellLocation: readyUpsellInformation.app_location ?? '',
                upsellType: readyUpsellInformation.upsell_type ?? '',
            },
        }));
    }, [
        enabled,
        readyUpsellInformation,
        readyUpsellInformation?.app_location,
        readyUpsellInformation?.event_metadata,
        readyUpsellInformation?.name,
        readyUpsellInformation?.upsell_type,
        readyUpsellInformation?.upsell_uuid,
        impression,
    ]);
    if (!sourcePlan.show) {
        return sourcePlan;
    }
    if (!visible) {
        return { status: 'blocked', show: false, reason: 'blocked_by_callsite' };
    }
    return sourcePlan;
};
//# sourceMappingURL=useSidebarUpsellSlotPlan.js.map